import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQItem[] = [
  {
    category: 'Dosage & Usage',
    question: 'What is the recommended daily dosage of moringa powder?',
    answer: 'For adults, we recommend 1–2 teaspoons (3–6g) per day. You can start with ½ teaspoon and gradually increase over a week or two to let your body adjust. It\'s best taken in the morning — added to warm water with honey, a smoothie, or stirred into porridge. Do not exceed 2 tablespoons (about 10g) per day. Always add moringa after cooking, never boil it, to preserve its nutrients.'
  },
  {
    category: 'Dosage & Usage',
    question: 'How should I take moringa powder — can I cook with it?',
    answer: 'Yes — moringa powder is very versatile! Add it to smoothies, fresh juices, warm water with honey and lemon, soups, stews (stir in after turning off heat), yoghurt, or oatmeal. Because heat above 70°C destroys some vitamins, always add moringa powder after cooking or into cold and warm (not boiling) drinks for maximum nutritional benefit.'
  },
  {
    category: 'Safety & Kids',
    question: 'Is moringa powder safe for children?',
    answer: 'Yes — moringa powder is safe and highly beneficial for children when given in age-appropriate amounts. For children aged 1–5, start with ¼ teaspoon (about 1g) per day mixed into food or juice. Children aged 6–12 can take ½ teaspoon daily. It\'s particularly valuable for growing children due to its exceptional calcium, iron, and vitamin A content which support bone development, healthy blood, and eyesight. Always consult your paediatrician if your child has existing health conditions.'
  },
  {
    category: 'Safety & Kids',
    question: 'Can pregnant or breastfeeding women take moringa powder?',
    answer: 'Moringa leaves are a traditional and nutritious food used across West Africa. The leaf powder is generally considered safe and even beneficial during breastfeeding, as it may support milk production. However, during pregnancy — especially the first trimester — it is advisable to consult your doctor or midwife first, as high doses are not recommended. A small daily amount (½ teaspoon) is typically considered safe, but professional guidance is always best.'
  },
  {
    category: 'Safety & Kids',
    question: 'Can moringa powder be taken with medication?',
    answer: 'Moringa is a food, not a drug, and is generally safe alongside a healthy diet. However, because moringa can support healthy blood pressure and blood sugar levels, people already taking medication for hypertension or diabetes should monitor their levels carefully and consult their doctor — the combined effect may require dosage adjustments. There are no known serious drug interactions, but it\'s always best to inform your healthcare provider when starting any new supplement.'
  },
  {
    category: 'Storage',
    question: 'How should I store moringa powder to keep it fresh?',
    answer: 'Store your moringa powder in a sealed, airtight container away from direct sunlight, heat, and moisture. A cool, dry cupboard or pantry works perfectly — you don\'t need to refrigerate it. Avoid leaving the jar open or near the stove. When stored correctly, our pure moringa powder has a shelf life of 24 months from production date. You\'ll know it\'s still fresh by its vibrant green colour and earthy, grassy aroma.'
  },
  {
    category: 'Storage',
    question: 'Does the powder lose its nutrients over time?',
    answer: 'All natural foods experience some gradual nutrient degradation over time, but our low-heat processing method significantly slows this process. When stored correctly (cool, dry, sealed, away from light), our moringa powder retains the vast majority of its nutrients for the full 24-month shelf life. For best results, we recommend consuming within 12 months of opening. Exposure to heat, light, or air speeds up nutrient loss, so always reseal after each use.'
  },
  {
    category: 'Quality & Certification',
    question: 'Is your moringa powder 100% pure? Are there any additives?',
    answer: 'Absolutely — our Pure Moringa Powder contains one ingredient: moringa leaves. No fillers, no additives, no preservatives, no artificial colours or flavours, and no added sugar. We grow our moringa trees in Ghana without synthetic pesticides or chemical fertilisers. After harvesting, the leaves are carefully dried using a low-heat method and milled into fine powder. Nothing is added at any stage of processing.'
  },
  {
    category: 'Quality & Certification',
    question: 'Where is your moringa grown and how is it processed?',
    answer: 'Our moringa is grown right here in Ghana — locally sourced, freshly harvested, and processed at our own facility to maintain full control over quality at every stage. After harvesting, leaves are shade-dried at low temperatures (below 60°C) to protect heat-sensitive vitamins like Vitamin C and folate. They are then milled into a fine powder, screened for quality, and sealed in airtight packaging. From our farm to your home — fully traceable and proudly Ghanaian.'
  },
  {
    category: 'Quality & Certification',
    question: 'How soon will I notice health benefits from taking moringa powder?',
    answer: 'Most people begin noticing improved energy levels and reduced fatigue within 1–2 weeks of consistent daily use. Improvements in digestion are often felt within the first week. Longer-term benefits — such as stronger immunity, healthier skin, better blood sugar balance, and improved iron levels — typically become noticeable after 4–8 weeks of regular use. Moringa is a food supplement, not a medicine. Consistency is key — daily use over several weeks gives the best results.'
  }
];

const categories = ['All', 'Dosage & Usage', 'Safety & Kids', 'Storage', 'Quality & Certification'];

export default function MoringaFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? faqs
    : faqs.filter(f => f.category === activeCategory);

  const categoryIcons: Record<string, string> = {
    'Dosage & Usage': 'ri-medicine-bottle-line',
    'Safety & Kids': 'ri-shield-check-line',
    'Storage': 'ri-archive-line',
    'Quality & Certification': 'ri-award-line',
  };

  return (
    <section className="py-20 bg-white">
      {/* Schema.org FAQPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map(faq => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
              }
            }))
          })
        }}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-green-100 text-green-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
            Moringa Powder FAQ
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Everything You Need to Know
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Honest answers on dosage, safety for children, storage, and what makes our moringa powder genuinely different.
          </p>
        </div>

        {/* Category filter pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-700'
              }`}
            >
              {cat !== 'All' && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${categoryIcons[cat]} text-sm`}></i>
                </span>
              )}
              {cat}
            </button>
          ))}
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {filtered.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`border rounded-xl overflow-hidden transition-all ${
                  isOpen ? 'border-green-300 bg-green-50/40' : 'border-gray-200 bg-white hover:border-green-200'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-start justify-between text-left px-6 py-5 gap-4 cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-bold transition-colors ${
                      isOpen ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      Q
                    </span>
                    <span className={`font-semibold text-base leading-snug transition-colors ${
                      isOpen ? 'text-green-800' : 'text-gray-900'
                    }`}>
                      {faq.question}
                    </span>
                  </div>
                  <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
                    <i className={`ri-add-line text-xl ${isOpen ? 'text-green-600' : 'text-gray-400'}`}></i>
                  </span>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 pt-1">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 bg-green-600 text-white text-xs font-bold">
                        A
                      </span>
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 rounded-2xl overflow-hidden relative">
          <img
            src="https://public.readdy.ai/ai/img_res/c44edd061e0980dadc38756702fab61e.jpg"
            alt="Moringa farm Ghana"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-800/70 to-green-700/60"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 px-10 py-10">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Still have questions? We're here to help.
              </h3>
              <p className="text-green-100 text-sm max-w-lg">
                Our team is happy to guide you on the right product, dosage, and usage for your specific needs — whether you're buying for yourself, your family, or in bulk.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href="https://paystack.shop/pay/v9-4hu3n50"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-white text-green-700 hover:bg-green-50 font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line mr-2"></i>
                Order Now
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-chat-3-line mr-2"></i>
                Ask a Question
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
